import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'

type PaginationProps = {
  /** Zero-based. */
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  /** Mono summary on the left — «1–20 из 128». */
  rangeLabel?: ReactNode
  className?: string
}

const CONTROL =
  'flex h-7 items-center justify-center rounded-lg border text-caption press disabled:pointer-events-none disabled:opacity-40'

/**
 * Numbered pages with arrows, sitting under the last row of a table.
 *
 * This replaces shadcn's link-based pagination outright: Verdant draws bordered
 * boxes with an accent plate on the current page, and the shadcn one is a row
 * of ghost buttons with an ellipsis. Every page is listed rather than elided —
 * pass a windowed `pageCount` if a dataset ever grows past a couple of dozen.
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  rangeLabel,
  className,
}: PaginationProps) {
  const { t } = useTranslation('common')

  return (
    <nav
      aria-label={t('ui.pages')}
      data-slot="pagination"
      className={cn(
        'flex items-center justify-between gap-3 text-caption text-muted-foreground',
        className,
      )}
    >
      {rangeLabel !== undefined && <span className="font-mono">{rangeLabel}</span>}

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          aria-label={t('ui.prevPage')}
          disabled={page === 0}
          onClick={() => {
            onPageChange(Math.max(0, page - 1))
          }}
          className={cn(
            CONTROL,
            'w-7 border-border-strong text-fg-secondary hover:bg-surface-2 hover:text-foreground',
          )}
        >
          <ChevronLeftIcon strokeWidth={1.8} className="size-2.75" />
        </button>

        {Array.from({ length: pageCount }, (_, index) => {
          const isCurrent = index === page

          return (
            <button
              key={index}
              type="button"
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => {
                onPageChange(index)
              }}
              className={cn(
                CONTROL,
                'min-w-7 px-2',
                isCurrent
                  ? 'border-primary/28 bg-primary/16 text-accent-text'
                  : 'border-border-strong text-fg-secondary hover:bg-surface-2 hover:text-foreground',
              )}
            >
              {index + 1}
            </button>
          )
        })}

        <button
          type="button"
          aria-label={t('ui.nextPage')}
          disabled={page >= pageCount - 1}
          onClick={() => {
            onPageChange(Math.min(pageCount - 1, page + 1))
          }}
          className={cn(
            CONTROL,
            'w-7 border-border-strong text-fg-secondary hover:bg-surface-2 hover:text-foreground',
          )}
        >
          <ChevronRightIcon strokeWidth={1.8} className="size-2.75" />
        </button>
      </div>
    </nav>
  )
}
